import { useCallback, useEffect, useMemo, useState } from 'react';
import { deriveInitialImportExperience } from '../viewmodels/parity/initialImportExperience';

const PRIMED_STORAGE_PREFIX = 'vylo:historical-import-banner:primed';
const SUCCESS_SEEN_STORAGE_PREFIX = 'vylo:historical-import-banner:success-seen';

const getBannerCycleKey = ({ householdId, onboardingImportStartedAt }) => (
  `${householdId ?? 'none'}:${onboardingImportStartedAt || 'unknown'}`
);

const readFlag = (storageKey) => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(storageKey) === '1';
};

const writeFlag = (storageKey) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey, '1');
};

const getPrimedStorageKey = (cycleKey) => `${PRIMED_STORAGE_PREFIX}:${cycleKey}`;
const getSuccessSeenStorageKey = (cycleKey) => `${SUCCESS_SEEN_STORAGE_PREFIX}:${cycleKey}`;

const getBannerExperience = (bankStatus) => deriveInitialImportExperience(bankStatus);

const useHistoricalImportBanner = ({ householdId, bankStatus }) => {
  const experience = useMemo(
    () => getBannerExperience(bankStatus),
    [bankStatus]
  );
  const cycleKey = useMemo(
    () => {
      const baseKey = getBannerCycleKey({
      householdId,
      onboardingImportStartedAt: bankStatus?.onboardingImportStartedAt || null,
      });
      return baseKey.includes('unknown') ? null : baseKey;
    },
    [bankStatus?.onboardingImportStartedAt, householdId]
  );
  const preparationPhase = experience.preparationStatus?.phase || 'idle';
  const activePreparation = experience.preparationStatus?.active === true;
  const preparationSuccess = experience.preparationStatus?.success === true;
  const [phase, setPhase] = useState(null);

  useEffect(() => {
    if (!cycleKey) {
      setPhase(null);
      return;
    }

    const primedStorageKey = getPrimedStorageKey(cycleKey);
    const successSeenStorageKey = getSuccessSeenStorageKey(cycleKey);

    if (activePreparation) {
      writeFlag(primedStorageKey);
      setPhase(preparationPhase);
      return;
    }

    if (
      preparationSuccess
      && readFlag(primedStorageKey)
      && !readFlag(successSeenStorageKey)
    ) {
      setPhase('success');
      return;
    }
    setPhase(null);
  }, [activePreparation, cycleKey, preparationPhase, preparationSuccess]);

  const dismissHistoricalImportBanner = useCallback(() => {
    setPhase(null);
  }, []);

  const acknowledgeHistoricalImportSuccess = useCallback(() => {
    if (!cycleKey) return;
    writeFlag(getSuccessSeenStorageKey(cycleKey));
    setPhase(null);
  }, [cycleKey]);

  return {
    historicalImportBannerPhase: phase,
    showHistoricalImportBanner: phase === 'historical_import' || phase === 'ai_review',
    dismissHistoricalImportBanner,
    acknowledgeHistoricalImportSuccess,
  };
};

export default useHistoricalImportBanner;
export { getBannerCycleKey, getBannerExperience };
